export enum TCustomCheckoutLayout {
  desktop = 1,
  mobile = 2,
}

export type TCustomCheckoutPagination = {
  items: TCustomCheckout[];
  page: number;
  rowsPerPage: number;
  totalPages: number;
  total: number;
};

export enum TCustomCheckoutComponentType {
  none = 0,
  checkout = 1,
  text = 2,
  image = 3,
  stopWatch = 4,
  layout1 = 5,
  layout1_2 = 6,
  layout2_3 = 7,
  list = 8,
  empty = 9,
}

export type TCustomCheckoutComponent = {
  name: string;
  icon: JSX.Element;
  type: TCustomCheckoutComponentType;
};

export type TCustomCheckoutComponentItemSX = {
  backgroundColor?: string;
  color?: string;
  height?: string | number;

  backgroundImage?: string;
  backgroundImageCode?: string;
  backgroundImageName?: string;
  backgroundImageExt?: string;
  backgroundImageFixedTop?: boolean;
  backgroundImageRepeat?: boolean;
  backgroundImageExpand?: boolean;
};

/** Campos base compartilhados por todos os tipos de componente */
export type TCustomCheckoutComponentItemBase = {
  id: string;
  name: string;
  order: number;
  sx: TCustomCheckoutComponentItemSX;
  title: string;
  subtitle: string;
  fixedTop: boolean;
  children: TCustomCheckoutComponentItem[];
};

/** Campos específicos de imagem */
export type TImageFields = {
  imageUrl?: string;
  imageCode?: string;
  imageName?: string;
  imageExt?: string;
};

/** Campos específicos de cronômetro */
export type TStopWatchFields = {
  hours?: number;
  minutes?: number;
  seconds?: number;
  milleseconds?: number;
};

/**
 * Tipo unificado de componente do checkout.
 * Mantém compatibilidade total com a API (formato flat),
 * mas oferece type guards para narrowing seguro em cada componente.
 */
export type TCustomCheckoutComponentItem = TCustomCheckoutComponentItemBase &
  TImageFields &
  TStopWatchFields & {
    type: TCustomCheckoutComponentType;
  };

// ─── Type Guards ─────────────────────────────────────────────────────
// Permitem narrowing seguro ao trabalhar com tipos específicos

export function isCheckoutItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.checkout;
}

export function isTextItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.text;
}

export function isImageItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.image;
}

export function isStopWatchItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.stopWatch;
}

export function isLayout2_3Item(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.layout2_3;
}

export function isListItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.list;
}

export function isEmptyItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.empty;
}

export function isNoneItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.none;
}

/** Retorna true se o componente é protegido (não pode ser removido/movido) */
export function isProtectedItem(item: TCustomCheckoutComponentItem): boolean {
  return (
    item.type === TCustomCheckoutComponentType.checkout ||
    item.type === TCustomCheckoutComponentType.layout2_3
  );
}

/** Retorna true se o componente é arrastável */
export function isDraggableItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type !== TCustomCheckoutComponentType.checkout;
}

/** Retorna true se o componente pode ser deletado */
export function isDeletableItem(item: TCustomCheckoutComponentItem): boolean {
  return (
    item.type !== TCustomCheckoutComponentType.checkout &&
    item.type !== TCustomCheckoutComponentType.layout2_3
  );
}

/** Retorna true se o componente é um container (aceita filhos via drop) */
export function isContainerItem(item: TCustomCheckoutComponentItem): boolean {
  return item.type === TCustomCheckoutComponentType.list;
}

export enum TModelType {
  none = 1,
  image = 2,
  imageText = 3,
}

export enum TClickAction {
  discount = 1,
  redirectUrl = 2,
  onlyClose = 3,
}

export type TPopupConfig = {
  modelType: TModelType;
  actionClick?: TClickAction;
  mainText?: string;
  auxText?: string;
  textButton?: string;
  imageUrl?: string;
  imageCode?: string;
  imageName?: string;
  imageExt?: string;
  actionValue?: string;
};

export type TCustomCheckoutComponentDevice = {
  settings: TCustomCheckoutComponentItemSX;
  components: TCustomCheckoutComponentItem[];
  popupConfig?: TPopupConfig;
};

export type TCustomCheckout = {
  id: string;
  name: string;
  productId: string;
  productName?: string;
  prices: string[];
  defaultValue: boolean;
  desktopLayout?: TCustomCheckoutComponentDevice;
  mobileLayout?: TCustomCheckoutComponentDevice;
};
